import { useMemo, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import Pagination from "@/components/base/Pagination";
import ArticleModal from "@/components/feature/ArticleModal";
import { articles } from "@/mocks/reviews";
import { films } from "@/mocks/films";
import { articleTags } from "@/constants/filters";
import type { Article } from "@/types/film";
import usePageMeta from "@/hooks/usePageMeta";

const PAGE_SIZE = 4;

export default function ReviewsPage() {
  const [tag, setTag] = useState("全部专题");
  const [page, setPage] = useState(1);
  const [activeArticle, setActiveArticle] = useState<Article | null>(null);

  usePageMeta(
    "影评专题 - 伦理视频",
    "伦理视频影评与解析专题，按家庭伦理、悬疑伦理、性别议题等标签分类，提供影片叙事结构、人物动机与伦理立场的深度解读文章。",
    "影评专题,影视解析,伦理影评,家庭伦理分析,悬疑伦理,伦理视频",
  );

  const featured = articles[0];
  const featuredFilm = films.find((film) => film.id === featured.filmId);

  const filtered = useMemo(
    () => (tag === "全部专题" ? articles : articles.filter((article) => article.tag === tag)),
    [tag],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleTagChange = (next: string) => {
    setTag(next);
    setPage(1);
  };

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Reviews & Analysis"
        title="影评与解析专题"
        description="我们把伦理影视拆成三个部分来看：故事如何被讲述，人物为什么这样选择，以及作品最终站在什么立场上。这里收录编辑部的长文解析，也欢迎你带着不同的看法来。"
        breadcrumbs={[{ label: "首页", to: "/" }, { label: "影评专题" }]}
      />

      <section className="w-full px-4 pt-8 md:px-10 md:pt-10">
        <div className="relative overflow-hidden rounded-lg bg-primary-950">
          {featuredFilm && (
            <img
              src={featuredFilm.backdrop}
              alt={`${featuredFilm.title}剧照`}
              title={`${featuredFilm.title} - 伦理视频影评专题`}
              className="absolute inset-0 h-full w-full object-cover object-center opacity-30"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/90 to-primary-950/50" />
          <div className="relative z-10 flex flex-col gap-4 p-5 md:flex-row md:items-end md:justify-between md:p-8">
            <div className="max-w-2xl">
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-background-200">
                <span className="rounded-full bg-accent-500 px-2.5 py-0.5 text-background-50">本期头条</span>
                <span className="rounded-full border border-background-50/30 px-2.5 py-0.5">{featured.tag}</span>
                <span>{featured.date}</span>
              </div>
              <h2 className="mt-3 font-heading text-xl font-semibold leading-snug text-background-50 md:text-3xl">
                {featured.title}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-background-300 clamp-3">{featured.excerpt}</p>
            </div>
            <button
              type="button"
              onClick={() => setActiveArticle(featured)}
              className="flex w-fit shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-accent-600 cursor-pointer"
            >
              阅读全文
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </section>

      <section className="w-full px-4 py-8 md:px-10 md:py-10">
        <div className="flex flex-col gap-4 border-b border-background-200 pb-5 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 flex items-center gap-1.5 text-xs text-foreground-500">
              <i className="ri-price-tag-3-line" />
              专题标签
            </span>
            {articleTags.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => handleTagChange(item)}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors cursor-pointer ${
                  tag === item
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
          <p className="shrink-0 text-xs text-foreground-400">
            共 {filtered.length} 篇 · 第 {currentPage} / {totalPages} 页
          </p>
        </div>

        {visible.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
            {visible.map((article, index) => (
              <article
                key={article.id}
                className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 md:p-6"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="rounded-sm bg-accent-100 px-2 py-0.5 text-[11px] text-accent-800">
                    {article.tag}
                  </span>
                  <span className="font-heading text-xs text-foreground-300">
                    {String((currentPage - 1) * PAGE_SIZE + index + 1).padStart(2, "0")}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => setActiveArticle(article)}
                  className="mt-3 text-left cursor-pointer"
                >
                  <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors hover:text-primary-600 md:text-lg">
                    {article.title}
                  </h3>
                </button>

                <p className="mt-2 flex-1 text-xs leading-relaxed text-foreground-600 clamp-3 md:text-sm">
                  {article.excerpt}
                </p>

                <div className="mt-5 flex items-center justify-between gap-3 border-t border-background-200 pt-4">
                  <div className="min-w-0 text-[11px] text-foreground-500">
                    <p>{article.author}</p>
                    <p className="mt-0.5 text-foreground-400">
                      {article.date} · 评《{article.filmTitle}》
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveArticle(article)}
                    className="flex shrink-0 items-center gap-1 whitespace-nowrap text-xs text-primary-600 transition-colors hover:text-accent-600 cursor-pointer"
                  >
                    阅读
                    <i className="ri-arrow-right-line" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-file-text-line text-2xl" />
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">该标签下暂无影评</h3>
            <p className="mt-2 text-sm text-foreground-600">换一个标签，或者查看全部专题内容。</p>
            <button
              type="button"
              onClick={() => handleTagChange("全部专题")}
              className="mt-5 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-refresh-line" />
              查看全部专题
            </button>
          </div>
        )}

        <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />

        <p className="mt-8 rounded-md bg-background-100 px-4 py-3 text-center text-[11px] leading-relaxed text-foreground-500">
          说明：本页影评均为演示文本，用于展示阅读与筛选交互，站内不提供评论提交与用户互动功能。
        </p>
      </section>

      <ArticleModal article={activeArticle} onClose={() => setActiveArticle(null)} />
    </SiteLayout>
  );
}